import {
  Icons,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Box,
  Stack,
  Tooltip,
  Typography,
  tokens,
} from '@cast/design-system';

import { useAbility } from '@cast/shared-ability-data-access';

import { useSuppressConfirmationDialog } from '../_hooks/useSuppressConfirmationDialog';
import { RestrictionCategory } from '../types';

type RestrictionsDetailsSectionProps = {
  restrictions: RestrictionCategory[];
  selectedItemId?: string;
  onSuppressRestrictionType: (restrictionType: string) => void;
};

export const RestrictionsDetailsSection = ({
  restrictions,
  selectedItemId,
  onSuppressRestrictionType,
}: RestrictionsDetailsSectionProps) => {
  const { openDialog: openConfirmationDialog } =
    useSuppressConfirmationDialog();
  const ability = useAbility();
  const canSuppress = ability.can(
    'view',
    'FeatureOptimizationConstraintsSilencing'
  );

  return (
    <Box
      overflow="auto"
      height="100%"
      pt={24}
      data-testid="restrictions-details-section"
    >
      <Stack gap={0}>
        {restrictions.map((category) => (
          <Stack key={category.title} gap={0}>
            <ListSubheader
              component="div"
              testId="category-header"
              sx={{
                px: 32,
                py: 6,
                letterSpacing: '0.5px',
                backgroundColor: tokens.theme.bg.structural.surfaceRaised,
                lineHeight: 'normal',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Typography variant="L10B" color="inherit">
                {category.title}
              </Typography>
              <Box sx={{ flex: 1 }} />
              {canSuppress && (
                <Tooltip title={`Suppress ${category.title}`}>
                  <IconButton
                    variant="ghost"
                    size="sm"
                    aria-label={`Suppress ${category.title}`}
                    testId={`suppress-restriction-type-button-${
                      category.restrictionType ?? category.title
                    }`}
                    onClick={(event) => {
                      event.stopPropagation();
                      const restrictionType =
                        category.restrictionType ?? category.title;

                      openConfirmationDialog({
                        testId: `suppress-restriction-type-confirmation-dialog-${restrictionType}`,
                        title: `Suppress ${category.title}`,
                        message: `Are you sure you want to suppress ${category.title} for this workload?`,
                        confirmButtonText: 'Yes',
                        cancelButtonText: 'No',
                        onConfirm: () =>
                          onSuppressRestrictionType(restrictionType),
                      });
                    }}
                  >
                    <Icons.BellSlash size={16} />
                  </IconButton>
                </Tooltip>
              )}
            </ListSubheader>

            <List
              size="medium"
              disablePadding
              component="div"
              testId="category-items"
            >
              {category.items.map((item) => {
                const itemId = item.itemId || '';
                const isSelected = selectedItemId === itemId;
                const isClickable = Boolean(item.onClick);

                return (
                  <ListItem
                    key={itemId}
                    multiline
                    alignItems="flex-start"
                    selected={isSelected}
                    disableHover={!isClickable}
                    onClick={item.onClick}
                    testId={`restriction-item-${itemId}`}
                    data-selected={isSelected}
                    sx={{
                      px: 32,
                      py: 8,
                      borderRadius: 0,
                      cursor: isClickable ? 'pointer' : 'default',
                      '&&.Mui-selected': {
                        backgroundColor:
                          tokens.theme.bg.selection.primarySubtle,
                        color: tokens.theme.fg.structural.primary,
                        '& .DsListItem-adornment-start': {
                          color: 'inherit',
                        },
                        '&:hover': {
                          backgroundColor:
                            tokens.theme.bg.selection.primarySubtleHover,
                        },
                      },
                    }}
                    startAdornment={
                      <ListItemIcon>
                        <Icons.Terminal size={16} />
                      </ListItemIcon>
                    }
                  >
                    <ListItemText
                      primary={item.title}
                      secondary={item.description}
                      primaryTypographyProps={{
                        variant: 'C12',
                        color: 'inherit',
                      }}
                      secondaryTypographyProps={{
                        color: tokens.theme.fg.structural.secondary,
                      }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};
